import type { UseQueryOptions, UseMutationMutateOptions, UseOpenapiOptions } from '../../types'
import { PlayerAccountClient } from '../../types'
import { useOpenapi, OpenapiError, useMutation, useQuery, useCoreConfig, useSessionExpiry, USER_INFO_KEY, CONTACT_VERIFICATION_STATUS_KEY } from '#imports'

const VERIFY_CONTACT_EXPECTED_CODES = [
    PlayerAccountClient.PublicErrorCodes.PA_1200,
    PlayerAccountClient.PublicErrorCodes.PA_1201,
    PlayerAccountClient.PublicErrorCodes.PA_1202,
    PlayerAccountClient.PublicErrorCodes.PA_1205,
    PlayerAccountClient.PublicErrorCodes.PA_1206,
]

function isPublicError(error: unknown, ...codes: PlayerAccountClient.PublicErrorCodes[]) {
    return OpenapiError.isError(error) && codes.includes(error.data?.code)
}

/**
 * @title usePlayerAccountClient
 * @see Docs https://player-account-prod.spilnu.dk/api/documentation
 */
export const usePlayerAccountClient = (options: Pick<UseOpenapiOptions, 'ignoreUnauthorized'> = {}) => {
    const { site } = useCoreConfig()
    const client = useOpenapi<PlayerAccountClient.paths>({
        baseUrl: '/gateway/player-account/api',
        service: 'playerAccount',
        ...options,
    })

    const session_expiry = useSessionExpiry()

    function useSetPhoneNumber() {
        return useMutation(async (body: PlayerAccountClient.components['schemas']['SetPhoneNumberRequest']) => {
            const { data } = await client.PUT('/public/user/set-phone-number', {
                body
            })

            return data
        })
    }

    function useSetPhoneNumberAndEmail() {
        return useMutation(async (body: PlayerAccountClient.components['schemas']['SetPhoneNumberAndEmailRequest']) => {
            const { data } = await client.PUT('/public/user/set-phone-number-and-email', {
                body: {
                    ...body,
                    brand: site.brand as PlayerAccountClient.Brand
                }
            })

            return data
        })
    }

    /** user/info without the query layer, for callers that run before it. */
    async function getUserInfo(signal?: AbortSignal) {
        const { data } = await client.GET('/public/user/info', {
            signal,
            params: { query: { brand: site.brand as PlayerAccountClient.Brand } },
        })

        return data
    }

    /**
     * User info query. For a full-featured version with isLoggedIn checks and more use useUserInfo @see {@link https://gitlab.cego.dk/spilnu/npm-packages/core/-/blob/mk/migrate-to-user-info/src/runtime/composables/data/useUserInfo.ts#L3}
     */
    const useGetUserInfo = (options: Pick<UseQueryOptions<any>, 'enabled' | 'lazy' | 'server'> = {}) => {
        const { enabled = !!session_expiry.value, lazy = true, server = false } = options

        const query = useQuery(({ signal }) => getUserInfo(signal), {
            key: USER_INFO_KEY,
            server,
            lazy,
            enabled
        })

        return query
    }

    function useGetOccupationTypes() {
        return useQuery(async ({ signal }) => {
            const { data } = await client.GET('/public/occupation/types', { signal })

            return data
        }, {
            key: 'playerAccount:occupation:types',
            server: false,
            lazy: true
        })
    }

    function useUpdateOccupation() {
        return useMutation(async (body: NonNullable<PlayerAccountClient.paths['/public/user/occupation']['post']['requestBody']>['content']['application/json']) => {
            const { data } = await client.POST('/public/user/occupation', {
                body
            })

            return data
        })
    }

    function useGetSalaryTypes() {
        return useQuery(async ({ signal }) => {
            const { data } = await client.GET('/public/salary/types', { signal })

            return data
        }, {
            key: 'playerAccount:salary:types',
            server: false,
            lazy: true
        })
    }

    function useUpdateUserSalary() {
        return useMutation(async (body: NonNullable<PlayerAccountClient.paths['/public/user/salary']['post']['requestBody']>['content']['application/json']) => {
            const { data } = await client.POST('/public/user/salary', {
                body
            })

            return data
        })
    }

    function useSendUserReport() {
        return useMutation(async () => {
            const { data } = await client.POST('/public/user/user-report', {
                body: {
                    brand: site.brand as PlayerAccountClient.Brand
                }
            })

            return data
        })
    }

    function useGetRealityCheckSetting(options: Pick<UseQueryOptions<any>, 'enabled' | 'lazy' | 'server'> = {}) {
        const { enabled = !!session_expiry.value, lazy = true, server = false } = options

        return useQuery(async ({ signal }) => {
            const { data } = await client.GET('/public/user/reality-check-setting', { signal })

            return data
        }, {
            key: 'playerAccount:realityCheck',
            enabled,
            server,
            lazy
        })
    }

    function useUpdateRealityCheckSetting() {
        return useMutation(async (body: NonNullable<PlayerAccountClient.components['schemas']['RealityCheckSettingData']>) => {
            const { data } = await client.POST('/public/user/reality-check-setting', {
                body
            })

            return data
        })
    }

    function useDeleteRealityCheckSetting(options: UseMutationMutateOptions = {}) {
        return useMutation(async () => {
            const { data } = await client.DELETE('/public/user/reality-check-setting')

            return data
        }, options)
    }

    function useContactVerificationStatus(options: Pick<UseQueryOptions<any>, 'enabled' | 'lazy' | 'server'> = {}) {
        const { enabled = !!session_expiry.value, lazy = true, server = false } = options

        return useQuery(async ({ signal }) => {
            const { data } = await client.GET('/public/user/contact-verification', { signal })

            return data
        }, {
            key: CONTACT_VERIFICATION_STATUS_KEY,
            enabled,
            server,
            lazy
        })
    }

    function useRequestContactVerification() {
        return useMutation(async () => {
            const { data } = await client.POST('/public/user/contact-verification/request', {
                body: {
                    type: PlayerAccountClient.ContactType.email,
                    brand: site.brand as PlayerAccountClient.Brand
                }
            })

            return data
        }, {
            errorClass: error => isPublicError(error, PlayerAccountClient.PublicErrorCodes.PA_1205) ? 'expected' : undefined
        })
    }

    /** Needs no login: the token from the mailed link is the proof. */
    function useVerifyContact() {
        return useMutation(async (token: string) => {
            const { data } = await client.POST('/public/user/contact-verification/verify', {
                body: { token }
            })

            return data
        }, {
            errorClass: error => isPublicError(error, ...VERIFY_CONTACT_EXPECTED_CODES) ? 'expected' : undefined
        })
    }

    function isOngoingGameError(error: unknown) {
        return OpenapiError.isError(error) && error.data?.code === PlayerAccountClient.PublicErrorCodes.PA_1400
    }

    function useDeleteUser(options: UseMutationMutateOptions = {}) {
        return useMutation(async () => {
            const { data } = await client.POST('/public/user/delete')

            return data
        }, {
            // Ongoing game sessions are a user condition, not a failure.
            errorClass: error => isOngoingGameError(error) ? 'expected' : undefined,
            ...options
        })
    }

    return {
        client,
        session_expiry,
        getUserInfo,
        useGetUserInfo,
        useSetPhoneNumber,
        useSetPhoneNumberAndEmail,
        useGetOccupationTypes,
        useUpdateOccupation,
        useGetRealityCheckSetting,
        useUpdateRealityCheckSetting,
        useDeleteRealityCheckSetting,
        useGetSalaryTypes,
        useUpdateUserSalary,
        useSendUserReport,
        useDeleteUser,
        isOngoingGameError,
        useContactVerificationStatus,
        useRequestContactVerification,
        useVerifyContact,
    }
}
